class FacetFiltersForm extends HTMLElement {
	/***
	 *
	 * 'labels' are used to match the visual filter label - it will match an array of values if multiple are provided. Note that this is a 'loose' match, meaning that 'Brown' filter labels will be matched by 'Dark Brown' in the 'labels' array as well.
	 *  labels: ['Brown', 'Dark Brown', 'Burgundy']
	 *
	 * 'hex' is the color value used for the swatch background. This is a single value.
	 *  hex: '#4D2C1D',
	 *
	 *  'gradient' is used for multi-color swatches. It is an array of hex values that will be used to create a gradient background. These are arranged top-to-bottom for the 'linear' option and top-right to bottom-left for the 'radial' option.
	 * 	 gradient: ['#ff0000', '#ffeb00', '#00ff0a', '#1b00ff'],
	 *
	 * 	'type' is used to specify the type of gradient. It can be 'linear' or 'radial'. If not specified, it defaults to 'radial'. Linear is top-to-bottom, while radial is top-right to bottom-left.
	 *   type: 'linear',
	 *   OR
	 * 	 type: 'radial',
	 *
	 *   HEX EXAMPLE:
	 * 	 {
	 * 		hex: '#4D2C1D',
	 * 		labels: ['Brown']
	 * 	 },
	 *
	 *   GRADIENT EXAMPLE:
	 * 	 {
	 * 		gradient: ['#ff0000', '#ffeb00', '#00ff0a', '#1b00ff'],
	 * 		type: 'linear',
	 * 		labels: ['Multi-Color', 'Rainbow']
	 * 	 },
	 *
	 * 	 For an array/JSON data structure all entries need to be followed by a comma, except the last item. Note above that each line is followed by a comma - except for the 'labels' line as it appears last.
	 *   This applies to items within the '[]' brackets as well as items within the '{}' brackets.
	 *
	 * 	 E.G.
	 *
	 * 	 [
	 * 		{
	 * 			hex: '#4D2C1D',
	 * 			labels: ['Brown']
	 * 		},
	 * 		{
	 * 			hex: '#8D929A',
	 * 			labels: ['Grey']
	 * 		},
	 * 		{
	 * 			hex: '#8B5D4B',
	 * 			labels: ['Bronze']
	 * 		}
	 * 	 ]
	 *
	 *   Improperly formatted lines will break the entire filtering mechanism so please edit with extreme care.
	 *
	 */
	static swatchData = [
		{
			hex: '#6D3D29',
			labels: ['Brown']
		},
		{
			hex: '#8D929A',
			labels: ['Grey']
		},
		{
			gradient: ['#FED396', '#84472A'],
			type: 'radial',
			labels: ['Bronze']
		},
		{
			hex: '#FFFFFF',
			labels: ['White']
		},
		{
			hex: '#000000',
			labels: ['Black']
		},
		{
			hex: '#FFFAF1',
			labels: ['Off White']
		},
		{
			hex: '#2E6489',
			labels: ['Blue']
		},
		{
			gradient: ['#FFFFFF', '#A3A4A6'],
			type: 'radial',
			labels: ['Silver']
		},
		{
			gradient: ['#F7E991', '#CB7F21'],
			type: 'radial',
			labels: ['Gold']
		},
		{
			hex: '#FF9A01',
			labels: ['Orange']
		},
		{
			hex: '#376029',
			labels: ['Green']
		},
		{
			hex: '#C70000',
			labels: ['Red']
		},
		{
			hex: '#FCD01B',
			labels: ['Yellow']
		},
		{
			gradient: ['#C0452C', '#FCD01B', '#376029', '#2E6489'],
			type: 'linear',
			labels: ['Multi-Color']
		}
	];
	constructor() {
		super();
		this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
		this.debouncedOnSubmit = debounce((event) => {
			this.onSubmitHandler(event);
		}, 800);

		const facetForm = this.querySelector('form');
		facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

		const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
		if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
	}

	connectedCallback() {
		this.collectionUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.collectionUpdate, (event) => {
			if (this.classList.contains('mobile')) return;
			// Fuzzy match as the id for facets includes facets- on top of the ID
			if (!this.dataset.id.includes(event.id)) return;
			const page = event.page;
			FacetFiltersForm.appendNextPage(page, this.dataset.id);
		});
		FacetFiltersForm.generateSwatchColors(this);
	}

	disconnectedCallback() {
		if (this.collectionUpdateUnsubscriber) this.collectionUpdateUnsubscriber();
	}

	static generateSwatchColors(element) {
		const swatchElements = element.querySelectorAll('[data-custom-hex]');
		swatchElements.forEach((element) => {
			const swatchElement = element.querySelector('.swatch');
			if (!swatchElement) return;
			const swatchLabel = element.getAttribute('title');
			const swatchData = FacetFiltersForm.swatchData.find((swatch) =>
				swatch.labels.includes(swatchLabel)
			);
			if (!swatchData) {
				swatchElement.setAttribute('style', `--swatch--background: #ffffff`);
				return;
			}
			swatchElement.setAttribute(
				'style',
				`--swatch--background: ${
					swatchData.hex ? swatchData.hex : swatchData.gradient.toString()
				}`
			);
			if (swatchData.type) {
				swatchElement.classList.add(`swatch--${swatchData.type}`);
			} else if (swatchData.gradient) {
				swatchElement.classList.add('swatch--radial');
			}
		});
	}

	static setListeners() {
		const onHistoryChange = (event) => {
			const searchParams = event.state
				? event.state.searchParams
				: FacetFiltersForm.searchParamsInitial;
			if (searchParams === FacetFiltersForm.searchParamsPrev) return;
			FacetFiltersForm.renderPage(searchParams, null, false);
		};
		window.addEventListener('popstate', onHistoryChange);
	}

	static toggleActiveFacets(disable = true) {
		document.querySelectorAll('.js-facet-remove').forEach((element) => {
			element.classList.toggle('disabled', disable);
		});
	}

	static appendNextPage(page, id) {
		if (!page) return;
		const sections = FacetFiltersForm.getSections();
		document
			.getElementById('ProductGridContainer')
			.querySelector('.collection')
			.classList.add('loading');

		sections.forEach((section) => {
			const url = `${window.location.pathname}?section_id=${section.section}&page=${page}&${FacetFiltersForm.searchParamsPrev}`;
			FacetFiltersForm.renderNextPageFromFetch(url, page, id);
		});
	}

	static renderPage(searchParams, event, updateURLHash = true) {
		FacetFiltersForm.searchParamsPrev = searchParams;
		const sections = FacetFiltersForm.getSections();
		const countContainer = document.getElementById('ProductCount');
		const countContainerDesktop = document.getElementById('ProductCountDesktop');
		const loadingSpinners = document.querySelectorAll(
			'.facets-container .loading__spinner, facet-filters-form .loading__spinner'
		);
		loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
		document
			.getElementById('ProductGridContainer')
			.querySelector('.collection')
			.classList.add('loading');
		if (countContainer) {
			countContainer.classList.add('loading');
		}
		if (countContainerDesktop) {
			countContainerDesktop.classList.add('loading');
		}

		sections.forEach((section) => {
			const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
			const filterDataUrl = (element) => element.url === url;

			FacetFiltersForm.filterData.some(filterDataUrl)
				? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
				: FacetFiltersForm.renderSectionFromFetch(url, event);
		});

		if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
	}

	static renderSectionFromFetch(url, event) {
		fetch(url)
			.then((response) => response.text())
			.then((responseText) => {
				const html = responseText;
				FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, {html, url}];
				FacetFiltersForm.renderFilters(html, event);
				FacetFiltersForm.renderProductGridContainer(html);
				FacetFiltersForm.renderProductCount(html);
				if (typeof initializeScrollAnimationTrigger === 'function')
					initializeScrollAnimationTrigger(html.innerHTML);
			});
	}

	static renderNextPageFromFetch(url, page, id) {
		fetch(url)
			.then((response) => response.text())
			.then((responseText) => {
				const html = responseText;
				FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, {html, url}];
				FacetFiltersForm.appendToProductGridContainer(html, page, id);
			});
	}

	static renderSectionFromCache(filterDataUrl, event) {
		const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
		FacetFiltersForm.renderFilters(html, event);
		FacetFiltersForm.renderProductGridContainer(html);
		FacetFiltersForm.renderProductCount(html);
		if (typeof initializeScrollAnimationTrigger === 'function')
			initializeScrollAnimationTrigger(html.innerHTML);
	}

	static appendToProductGridContainer(html, page, id) {
		const markup = new DOMParser().parseFromString(html, 'text/html');
		const products = Array.from(markup.querySelectorAll('#product-grid .grid__item'));
		const fragment = document.createDocumentFragment();
		products.forEach((product) => {
			fragment.appendChild(product);
		});
		document.getElementById('product-grid').appendChild(fragment);
		document
			.getElementById('ProductGridContainer')
			.querySelector('.collection')
			.classList.remove('loading');
		const showMore = markup.querySelector('collection-show-more');
		document
			.getElementById('ProductGridContainer')
			.querySelector('collection-show-more')
			.replaceWith(showMore);
		publish(PUB_SUB_EVENTS.collectionLoaded, {
			source: 'FacetFiltersForm',
			id: id,
			page: page
		});
	}

	static renderProductGridContainer(html) {
		document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
			.parseFromString(html, 'text/html')
			.getElementById('ProductGridContainer').innerHTML;

		document
			.getElementById('ProductGridContainer')
			.querySelectorAll('.scroll-trigger')
			.forEach((element) => {
				element.classList.add('scroll-trigger--cancel');
			});
	}

	static renderProductCount(html) {
		const count = new DOMParser()
			.parseFromString(html, 'text/html')
			.getElementById('ProductCount').innerHTML;
		const container = document.getElementById('ProductCount');
		const containerDesktop = document.getElementById('ProductCountDesktop');
		container.innerHTML = count;
		container.classList.remove('loading');
		if (containerDesktop) {
			containerDesktop.innerHTML = count;
			containerDesktop.classList.remove('loading');
		}
		const loadingSpinners = document.querySelectorAll(
			'.facets-container .loading__spinner, facet-filters-form .loading__spinner'
		);
		loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
	}

	static renderFilters(html, event) {
		const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
		FacetFiltersForm.generateSwatchColors(parsedHTML);
		const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
			'#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
		);
		const facetDetailsElementsFromDom = document.querySelectorAll(
			'#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
		);

		// Remove facets that are no longer returned from the server
		Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
			if (
				!Array.from(facetDetailsElementsFromFetch).some(({id}) => currentElement.id === id)
			) {
				currentElement.remove();
			}
		});

		const matchesId = (element) => {
			const jsFilter = event ? event.target.closest('.js-filter') : undefined;
			return jsFilter ? element.id === jsFilter.id : false;
		};

		const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter(
			(element) => !matchesId(element)
		);
		const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

		facetsToRender.forEach((elementToRender, index) => {
			const currentElement = document.getElementById(elementToRender.id);
			// Element already rendered in the DOM so just update the innerHTML
			if (currentElement) {
				document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
			} else {
				if (index > 0) {
					const {className: previousElementClassName, id: previousElementId} =
						facetsToRender[index - 1];
					// Same facet type (eg horizontal/vertical or drawer/mobile)
					if (elementToRender.className === previousElementClassName) {
						document.getElementById(previousElementId).after(elementToRender);
						return;
					}
				}

				if (elementToRender.parentElement) {
					document
						.querySelector(`#${elementToRender.parentElement.id} .js-filter`)
						.before(elementToRender);
				}
			}
		});

		FacetFiltersForm.renderActiveFacets(parsedHTML);
		FacetFiltersForm.renderAdditionalElements(parsedHTML);

		if (countsToRender) {
			const closestJSFilterID = event.target.closest('.js-filter').id;

			if (closestJSFilterID) {
				FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
				FacetFiltersForm.renderMobileCounts(
					countsToRender,
					document.getElementById(closestJSFilterID)
				);

				const newFacetDetailsElement = document.getElementById(closestJSFilterID);
				const newElementSelector = newFacetDetailsElement.classList.contains(
					'mobile-facets__details'
				)
					? `.mobile-facets__close-button`
					: `.facets__summary`;
				const newElementToActivate =
					newFacetDetailsElement.querySelector(newElementSelector);

				const isTextInput = event.target.getAttribute('type') === 'text';

				if (newElementToActivate && !isTextInput) newElementToActivate.focus();
			}
		}
	}

	static renderActiveFacets(html) {
		const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

		activeFacetElementSelectors.forEach((selector) => {
			const activeFacetsElement = html.querySelector(selector);
			if (!activeFacetsElement) return;
			const hasFacets = activeFacetsElement.querySelector('.active-facets__button');
			document
				.querySelector(selector)
				.classList.toggle('active-facets--populated', hasFacets);
			document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
		});

		FacetFiltersForm.toggleActiveFacets(false);
	}

	static renderAdditionalElements(html) {
		const mobileElementSelectors = [
			'.mobile-facets__open',
			'.mobile-facets__count',
			'.sorting'
		];

		mobileElementSelectors.forEach((selector) => {
			if (!html.querySelector(selector)) return;
			document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
		});

		document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
	}

	static renderCounts(source, target) {
		const targetSummary = target.querySelector('.facets__summary');
		const sourceSummary = source.querySelector('.facets__summary');

		if (sourceSummary && targetSummary) {
			targetSummary.outerHTML = sourceSummary.outerHTML;
		}

		const targetHeaderElement = target.querySelector('.facets__header');
		const sourceHeaderElement = source.querySelector('.facets__header');

		if (sourceHeaderElement && targetHeaderElement) {
			targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
		}

		const targetWrapElement = target.querySelector('.facets-wrap');
		const sourceWrapElement = source.querySelector('.facets-wrap');

		if (sourceWrapElement && targetWrapElement) {
			const isShowingMore = Boolean(
				target.querySelector('show-more-button .label-show-more.hidden')
			);
			if (isShowingMore) {
				sourceWrapElement
					.querySelectorAll('.facets__item.hidden')
					.forEach((hiddenItem) =>
						hiddenItem.classList.replace('hidden', 'show-more-item')
					);
			}

			targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
		}
	}

	static renderMobileCounts(source, target) {
		const targetFacetsList = target.querySelector('.mobile-facets__list');
		const sourceFacetsList = source.querySelector('.mobile-facets__list');

		if (sourceFacetsList && targetFacetsList) {
			targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
		}
	}

	static updateURLHash(searchParams) {
		history.pushState(
			{searchParams},
			'',
			`${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
		);
	}

	static getSections() {
		return [
			{
				section: document.getElementById('product-grid').dataset.id
			}
		];
	}

	createSearchParams(form) {
		const formData = new FormData(form);
		return new URLSearchParams(formData).toString();
	}

	onSubmitForm(searchParams, event) {
		FacetFiltersForm.renderPage(searchParams, event);
	}

	onSubmitHandler(event) {
		event.preventDefault();
		const sortFilterForms = document.querySelectorAll('facet-filters-form form');
		if (event.srcElement.className == 'mobile-facets__checkbox') {
			const searchParams = this.createSearchParams(event.target.closest('form'));
			this.onSubmitForm(searchParams, event);
		} else {
			const forms = [];
			const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

			sortFilterForms.forEach((form) => {
				if (!isMobile) {
					if (
						form.id === 'FacetSortForm' ||
						form.id === 'FacetFiltersForm' ||
						form.id === 'FacetSortDrawerForm'
					) {
						forms.push(this.createSearchParams(form));
					}
				} else if (form.id === 'FacetFiltersFormMobile') {
					forms.push(this.createSearchParams(form));
				}
			});
			this.onSubmitForm(forms.join('&'), event);
		}
	}

	onActiveFilterClick(event) {
		event.preventDefault();
		FacetFiltersForm.toggleActiveFacets();
		const url =
			event.currentTarget.href.indexOf('?') == -1
				? ''
				: event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
		FacetFiltersForm.renderPage(url);
	}
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
	constructor() {
		super();
		this.querySelectorAll('input').forEach((element) => {
			element.addEventListener('change', this.onRangeChange.bind(this));
			element.addEventListener('keydown', this.onKeyDown.bind(this));
		});
		this.setMinAndMaxValues();
	}

	onRangeChange(event) {
		this.adjustToValidValues(event.currentTarget);
		this.setMinAndMaxValues();
	}

	onKeyDown(event) {
		if (event.metaKey) return;

		const pattern =
			/[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
		if (!event.key.match(pattern)) event.preventDefault();
	}

	setMinAndMaxValues() {
		const inputs = this.querySelectorAll('input');
		const minInput = inputs[0];
		const maxInput = inputs[1];
		if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
		if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
		if (minInput.value === '') maxInput.setAttribute('data-min', 0);
		if (maxInput.value === '')
			minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
	}

	adjustToValidValues(input) {
		const value = Number(input.value);
		const min = Number(input.getAttribute('data-min'));
		const max = Number(input.getAttribute('data-max'));

		if (value < min) input.value = min;
		if (value > max) input.value = max;
	}
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
	constructor() {
		super();
		const facetLink = this.querySelector('a');
		facetLink.setAttribute('role', 'button');
		facetLink.addEventListener('click', this.closeFilter.bind(this));
		facetLink.addEventListener('keyup', (event) => {
			event.preventDefault();
			if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
		});
	}

	closeFilter(event) {
		event.preventDefault();
		const form =
			this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
		form.onActiveFilterClick(event);
	}
}

customElements.define('facet-remove', FacetRemove);
